import { Link, useLocation } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import logoColor from "@/assets/kslau-logo-horizontal-color.png.asset.json";
import logoWhite from "@/assets/kslau-logo-horizontal-white.png.asset.json";
import iconColor from "@/assets/kslau-icon-color.png.asset.json";

import { firm } from "@/lib/content";
import { cn } from "@/lib/utils";
import { useSiteNav, visibleNavItems, type MegaMenuData } from "@/lib/hooks/use-site-nav";
import type { NavItem, NavItemType } from "@/lib/navigation/types";
import { HeaderSearch } from "./HeaderSearch";
import { collectTopInsightCategories } from "@/lib/insights/categories";

type PanelKey = "services" | "insights" | "stories" | null;

const storyTopics = [
  { label: "Tax", to: "/client-stories" as const, search: { topic: "Tax" } },
  { label: "Business Advisory", to: "/client-stories" as const, search: { topic: "Business Advisory" } },
  { label: "Operations", to: "/client-stories" as const, search: { topic: "Operations" } },
  { label: "Community", to: "/client-stories" as const, search: { topic: "Community" } },
] as const;

function navTypeToPanel(type: NavItemType): Exclude<PanelKey, null> | null {
  if (type === "mega_services") return "services";
  if (type === "mega_insights") return "insights";
  if (type === "mega_stories") return "stories";
  return null;
}

function isMegaRouteActive(type: NavItemType, pathname: string): boolean {
  if (type === "mega_services") return pathname.startsWith("/services");
  if (type === "mega_insights") return pathname.startsWith("/insights");
  if (type === "mega_stories") return pathname.startsWith("/client-stories");
  return false;
}

export function SiteHeader() {
  const { headerMenu, megaMenu } = useSiteNav();
  const navItems = visibleNavItems(headerMenu);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState<PanelKey>(null);
  const [active, setActive] = useState<PanelKey>(null);
  const [scrolled, setScrolled] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const location = useLocation();

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled && active === null;

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    setActive(null);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  function openPanel(k: Exclude<PanelKey, null>) {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setActive(k);
  }
  function scheduleClose() {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setActive(null), 160);
  }

  const linkBase = cn(
    "relative inline-flex items-center gap-1 px-2 py-2 text-base font-medium transition",
    transparent ? "text-white/90 hover:text-white" : "text-primary hover:text-gold",
  );
  const linkActive = transparent
    ? "after:absolute after:inset-x-2 after:-bottom-[14px] after:h-[2px] after:bg-white text-white"
    : "after:absolute after:inset-x-2 after:-bottom-[14px] after:h-[2px] after:bg-accent text-gold";

  return (
    <>
      <header
        className="fixed inset-x-0 top-0 z-50"
        onMouseLeave={scheduleClose}
      >
        <div
          className={cn(
            "transition-colors",
            transparent ? "bg-transparent" : "",
          )}
          style={
            transparent
              ? undefined
              : {
                  backgroundColor: "color-mix(in oklab, white 92%, transparent)",
                  backdropFilter: "saturate(180%) blur(14px)",
                  WebkitBackdropFilter: "saturate(180%) blur(14px)",
                }
          }
        >
          <div className="container-page flex h-14 items-center gap-3 md:h-16">
            <Link
              to="/"
              className="flex shrink-0 items-center gap-2.5"
              aria-label={`${firm.name} home`}
              onMouseEnter={scheduleClose}
            >
              <img
                src={transparent ? logoWhite.url : iconColor.url}
                alt={firm.name}
                fetchPriority="low"
                decoding="async"
                className={cn("w-auto lg:hidden", transparent ? "h-10" : "h-8")}
              />
              <img
                src={transparent ? logoWhite.url : logoColor.url}
                alt={firm.name}
                fetchPriority="low"
                decoding="async"
                className={cn(
                  "hidden w-auto lg:block transition-all duration-300",
                  transparent ? "h-11 lg:h-12" : "h-8 lg:h-9",
                )}
              />
            </Link>

            <nav className="mx-auto hidden items-center gap-6 lg:flex">
              {navItems.map((item) => {
                const panel = navTypeToPanel(item.type);
                if (panel) {
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onMouseEnter={() => openPanel(panel)}
                      onFocus={() => openPanel(panel)}
                      onClick={() => openPanel(panel)}
                      aria-expanded={active === panel}
                      className={cn(
                        linkBase,
                        (active === panel || isMegaRouteActive(item.type, location.pathname)) &&
                          linkActive,
                      )}
                    >
                      {item.label}
                      <ChevronDown
                        className={cn(
                          "h-3.5 w-3.5 transition",
                          active === panel && "rotate-180",
                        )}
                      />
                    </button>
                  );
                }
                return (
                  <Link
                    key={item.id}
                    to={item.href ?? "/"}
                    onMouseEnter={scheduleClose}
                    className={linkBase}
                    activeProps={{ className: cn(linkBase, linkActive) }}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            <div className="ml-auto hidden items-center gap-1 lg:flex">
              <div onMouseEnter={scheduleClose}>
                <HeaderSearch transparent={transparent} />
              </div>
              <Link
                to="/contact"
                onMouseEnter={scheduleClose}
                className={cn(
                  "inline-flex h-9 items-center justify-center rounded-md px-4 text-sm font-semibold uppercase tracking-wider transition",
                  transparent
                    ? "bg-primary text-white hover:bg-primary/90"
                    : "bg-primary text-primary-foreground hover:bg-primary/90",
                )}
              >
                Contact Us
              </Link>
            </div>

            <button
              type="button"
              aria-label="Open menu"
              onClick={() => setMobileOpen(true)}
              className={cn(
                "ml-auto inline-flex h-10 w-10 items-center justify-center rounded-full lg:hidden",
                transparent ? "text-white" : "text-primary",
              )}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>

        {active && (
          <div
            className="hidden bg-background shadow-[0_24px_50px_-30px_rgb(32_56_112/0.35)] lg:block"
            onMouseEnter={() => openPanel(active)}
          >
            <div className="container-page py-10">
              {active === "services" && <ServicesPanel megaMenu={megaMenu} />}
              {active === "insights" && <InsightsPanel megaMenu={megaMenu} />}
              {active === "stories" && <StoriesPanel megaMenu={megaMenu} />}
            </div>
          </div>
        )}
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            aria-label="Close menu"
            className="absolute inset-0 bg-primary/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute inset-x-3 top-3 max-h-[92vh] overflow-y-auto rounded-xl bg-background p-5 shadow-2xl">
            <div className="flex items-center justify-between">
              <img src={logoColor.url} alt={firm.name} className="h-8 w-auto" />
              <button
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-primary"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="mt-5 flex flex-col">
              {navItems.map((item) => {
                const panel = navTypeToPanel(item.type);
                if (panel) {
                  return (
                    <MobileMegaGroup
                      key={item.id}
                      item={item}
                      panel={panel}
                      open={mobileGroup === panel}
                      onToggle={() =>
                        setMobileGroup((g) => (g === panel ? null : panel))
                      }
                      onClose={() => setMobileOpen(false)}
                      megaMenu={megaMenu}
                    />
                  );
                }
                return (
                  <Link
                    key={item.id}
                    to={item.href ?? "/"}
                    onClick={() => setMobileOpen(false)}
                    className="rounded-md px-3 py-3 text-lg font-medium text-foreground/80"
                    activeProps={{ className: "bg-primary/10 text-primary" }}
                  >
                    {item.label}
                  </Link>
                );
              })}
              <Link
                to="/contact"
                onClick={() => setMobileOpen(false)}
                className="mt-2 inline-flex justify-center rounded-md border border-gold bg-transparent px-4 py-3 text-base font-semibold text-gold hover:bg-gold/10"
              >
                Contact Us
              </Link>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}

function MobileMegaGroup({
  item,
  panel,
  open,
  onToggle,
  onClose,
  megaMenu,
}: {
  item: NavItem;
  panel: Exclude<PanelKey, null>;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  megaMenu: MegaMenuData;
}) {
  const { services, insights, stories } = megaMenu;
  const insightCategories = useMemo(() => collectTopInsightCategories(insights, 5), [insights]);

  return (
    <MobileGroup label={item.label} open={open} onToggle={onToggle}>
      {panel === "services" && (
        <>
          <Link
            to="/services"
            onClick={onClose}
            className="block rounded-lg px-2 py-2 text-base font-semibold text-primary"
          >
            All services
          </Link>
          {services.map((s) => (
            <Link
              key={s.slug}
              to="/services/$slug"
              params={{ slug: s.slug }}
              onClick={onClose}
              className="block rounded-lg px-2 py-2 text-base text-foreground/75"
            >
              {s.name}
            </Link>
          ))}
        </>
      )}
      {panel === "insights" && (
        <>
          <Link
            to="/insights"
            onClick={onClose}
            className="block rounded-lg px-2 py-2 text-base font-semibold text-primary"
          >
            All insights
          </Link>
          {insightCategories.map((label) => (
            <Link
              key={label}
              to="/insights"
              search={{ category: label }}
              onClick={onClose}
              className="block rounded-lg px-2 py-2 text-sm font-medium text-gold"
            >
              {label}
            </Link>
          ))}
          {insights.slice(0, 4).map((a) => (
            <Link
              key={a.slug}
              to="/insights/$slug"
              params={{ slug: a.slug }}
              onClick={onClose}
              className="block rounded-lg px-2 py-2 text-base text-foreground/75"
            >
              {a.title}
            </Link>
          ))}
        </>
      )}
      {panel === "stories" && (
        <>
          <Link
            to="/client-stories"
            onClick={onClose}
            className="block rounded-lg px-2 py-2 text-base font-semibold text-primary"
          >
            All client stories
          </Link>
          {stories.slice(0, 4).map((s) => (
            <Link
              key={s.slug}
              to="/client-stories/$slug"
              params={{ slug: s.slug }}
              onClick={onClose}
              className="block rounded-lg px-2 py-2 text-base text-foreground/75"
            >
              {s.title}
            </Link>
          ))}
        </>
      )}
    </MobileGroup>
  );
}

function MobileGroup({
  label,
  open,
  onToggle,
  children,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between rounded-md px-3 py-3 text-lg font-medium text-foreground/80"
      >
        {label}
        <ChevronDown className={cn("h-4 w-4 transition", open && "rotate-180")} />
      </button>
      {open && <div className="ml-3 mb-2 pl-3">{children}</div>}
    </div>
  );
}

function ServicesPanel({ megaMenu }: { megaMenu: MegaMenuData }) {
  const { services } = megaMenu;
  return (
    <div>
      <div className="grid gap-x-10 gap-y-2 md:grid-cols-4">
        {services.map((cat) => (
          <div key={cat.slug}>
            <Link
              to="/services/$slug"
              params={{ slug: cat.slug }}
              className="block text-base font-bold text-primary hover:underline"
            >
              {cat.name}
            </Link>
            <ul className="mt-3 space-y-2">
              {cat.items.slice(0, 6).map((it) => (
                <li key={it.name}>
                  <Link
                    to="/services/$slug"
                    params={{ slug: cat.slug }}
                    className="block text-sm leading-snug text-foreground/70 hover:text-primary"
                  >
                    {it.name}
                  </Link>
                </li>
              ))}
              {cat.items.length > 6 && (
                <li className="text-xs uppercase tracking-wider text-muted-foreground">
                  +{cat.items.length - 6} more
                </li>
              )}
            </ul>
          </div>
        ))}
      </div>
      <div className="mt-8 pt-5">
        <Link
          to="/services"
          className="inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.14em] text-primary hover:underline"
        >
          View all services <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}

function InsightsPanel({ megaMenu }: { megaMenu: MegaMenuData }) {
  const { insights } = megaMenu;
  const topCategories = useMemo(() => collectTopInsightCategories(insights, 5), [insights]);
  return (
    <div className="grid gap-10 md:grid-cols-[2fr_1fr]">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Latest articles
        </p>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {insights.slice(0, 4).map((a) => (
            <li key={a.slug}>
              <Link
                to="/insights/$slug"
                params={{ slug: a.slug }}
                className="block rounded-lg p-2 hover:bg-primary/5"
              >
                <p className="text-xs font-semibold uppercase tracking-wider text-gold">
                  {a.category}
                </p>
                <p className="mt-1 text-base font-bold leading-snug text-primary">{a.title}</p>
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Topics
        </p>
        <ul className="mt-4 space-y-2">
          {topCategories.map((label) => (
            <li key={label}>
              <Link
                to="/insights"
                search={{ category: label }}
                className="block text-sm text-foreground/75 hover:text-primary"
              >
                {label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div className="md:col-span-2 pt-5">
        <Link
          to="/insights"
          className="inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.14em] text-primary hover:underline"
        >
          View all insights <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}

function StoriesPanel({ megaMenu }: { megaMenu: MegaMenuData }) {
  const { stories } = megaMenu;
  return (
    <div className="grid gap-10 md:grid-cols-[2fr_1fr]">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Latest client stories
        </p>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {stories.slice(0, 4).map((s) => (
            <li key={s.slug}>
              <Link
                to="/client-stories/$slug"
                params={{ slug: s.slug }}
                className="block rounded-lg p-2 hover:bg-primary/5"
              >
                <p className="text-xs font-semibold uppercase tracking-wider text-gold">
                  {s.industry}
                </p>
                <p className="mt-1 text-base font-bold leading-snug text-primary">{s.title}</p>
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Topics
        </p>
        <ul className="mt-4 space-y-2">
          {storyTopics.map((t) => (
            <li key={t.label}>
              <Link
                to={t.to}
                search={t.search}
                className="block text-sm text-foreground/75 hover:text-primary"
              >
                {t.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div className="md:col-span-2 pt-5">
        <Link
          to="/client-stories"
          className="inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.14em] text-primary hover:underline"
        >
          View all client stories <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}
